/*
  Clients only need to get feature values. This avoids including the entire ab-testing dependency.
 */

import type { ComputedRef } from "vue";

import { computed } from "vue";

import type { SentryInstance } from "@solvari/utils";

import { getSentry } from "@solvari/utils";

import type { PageAttributes } from "@/service/attributes.ts";

/**
 * Returns a Sentry instance configured for A/B testing tracking.
 * Tags all events with the 'mkt-ab-testing' project for easier filtering.
 *
 * @returns Sentry instance with A/B testing project tag
 * @example const sentry = getSentryAbTesting(); sentry.captureException(error);
 */
function getSentryAbTesting(): SentryInstance {
  return getSentry({ tags: { project: "mkt-ab-testing" } });
}

/**
 * Retrieves a typed feature value from the GrowthBook A/B testing instance.
 * Returns null if GrowthBook is not initialized or feature doesn't exist.
 *
 * @param key - Feature key defined in GrowthBook
 * @param defaultValue
 * @returns Typed feature value or null
 * @example const variant = getAbTestFeature<string>('homepage-hero-variant');
 */
function getAbTestFeature<Type>(
  key: string,
  defaultValue: Type | null = null,
): ComputedRef<Type | null> {
  return computed(
    () =>
      (globalThis.abTesting?.getFeatureValue(key, null) as Type | null) ??
      defaultValue,
  );
}

function setAbTestAttribute<Key extends keyof PageAttributes>(
  key: Key,
  value: PageAttributes[Key],
) {
  const attributes = globalThis.abTestingAttributes;
  if (attributes) {
    attributes[key] = value;
  }
}

export { getAbTestFeature, getSentryAbTesting, setAbTestAttribute };
